// src/pages/Landing.tsx
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "@/styles/marketing.css";
import MarketingHeader, { APP_STORE_URL } from "@/components/marketing/MarketingHeader";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import TrainingShots from "@/components/marketing/TrainingShots";
import { ENV } from "@/lib/env";

const TELEMETRY = [
  "Workouts analysed — 4,000+",
  "Sport types — 06",
  "Active athletes — 500+",
  "Readiness engine — sleep · HRV · load · nutrition",
  "Integrations — Garmin / WHOOP / Apple Health / Concept2",
  "Coach — in-app · 24/7",
];

const VITALS = [
  { file: "vitals-rhr.webp", caption: "Resting HR — Garmin", alt: "Resting heart rate modal with 30-day baseline chart and a coach insight explaining what it means for training" },
  { file: "vitals-hrv.webp", caption: "HRV — WHOOP", alt: "HRV modal showing 81ms against a personal 77–95ms range, with a coach insight on the upward trend" },
  { file: "vitals-spo2.webp", caption: "Blood oxygen — WHOOP", alt: "Blood oxygen modal showing 97% against a personal range, with a coach insight" },
  { file: "vitals-resp.webp", caption: "Respiratory rate — WHOOP", alt: "Respiratory rate modal showing 15.8rpm against baseline, with a coach insight" },
];

const SPEC = [
  { id: "01", name: "AI coach", desc: "In the app, 24/7. Reads your sleep, sessions and meals before it answers.", tag: "Chat" },
  { id: "02", name: "Training programs", desc: "Running, cycling, swimming, triathlon, CrossFit, HYROX. Paces and loads computed from your PBs.", tag: "6 sports" },
  { id: "03", name: "Nutrition logging", desc: "Barcode, photo, voice or search. Targets recalculated daily from training and recovery.", tag: "4 capture paths" },
  { id: "04", name: "Readiness", desc: "Garmin and WHOOP sync. Sleep, HRV and load rolled into one morning score.", tag: "Wearables" },
  { id: "05", name: "Live tracking", desc: "Concept2, Echo Bike, treadmills and HR straps streaming over Bluetooth mid-session.", tag: "Bluetooth" },
  { id: "06", name: "Progression", desc: "XP, streaks, PBs and leaderboards. The boring-but-vital consistency layer.", tag: "Social" },
];

function SectionMark({ index, label }: { index: string; label: string }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <span className="mk-mono text-[13px] font-medium" style={{ color: "var(--mk-signal)" }}>
        {index}
      </span>
      <span className="mk-label">{label}</span>
      <span className="flex-1 h-px" style={{ background: "var(--mk-line)" }} />
    </div>
  );
}

function FeatureSection({
  index,
  label,
  title,
  children,
  mock,
  flip = false,
}: {
  index: string;
  label: string;
  title: string;
  children: React.ReactNode;
  mock: React.ReactNode;
  flip?: boolean;
}) {
  return (
    <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
      <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center ${
            flip ? "lg:[direction:rtl]" : ""
          }`}
        >
          <div className="lg:[direction:ltr]">
            <SectionMark index={index} label={label} />
            <h2 className="mk-display mb-6" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
              {title}
            </h2>
            <div className="text-[15.5px] leading-relaxed max-w-lg" style={{ color: "var(--mk-muted)" }}>
              {children}
            </div>
          </div>
          <div className="flex justify-center lg:[direction:ltr]">{mock}</div>
        </div>
      </div>
    </section>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-7 flex flex-col">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex gap-4 items-baseline py-3 border-t text-[14px]"
          style={{ borderColor: "var(--mk-line)", color: "var(--mk-muted)" }}
        >
          <span className="mk-mono text-[10px] flex-shrink-0" style={{ color: "var(--mk-signal)" }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

// Real app captures, revealed one by one as the rail scrolls into view.
function VitalsRail() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 items-start"
    >
      {VITALS.map((v, i) => (
        <figure
          key={v.file}
          className="mk-vital flex flex-col items-center"
          style={{
            transitionDelay: `${i * 170}ms`,
            opacity: visible ? 1 : 0,
            transform: visible ? "none" : "translateY(32px)",
          }}
        >
          <img
            src={`/screens/${v.file}`}
            alt={v.alt}
            loading="lazy"
            className="w-full max-w-[300px] block"
            onError={(e) => {
              // Asset not uploaded yet — collapse the slot instead of a broken glyph
              (e.currentTarget.parentElement as HTMLElement).style.display = "none";
            }}
          />
          <figcaption className="mk-label mt-4 text-center" style={{ fontSize: 10 }}>
            {v.caption}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

// Self-contained partner-dashboard vignette; the real product screenshots
// rotate too often to keep a static asset honest.
function PartnerDashboardMock() {
  const clients = [
    { name: "S. Turner", sport: "CrossFit", readiness: 84, trend: "+2.1%" },
    { name: "J. Reeves", sport: "Triathlon", readiness: 61, trend: "-0.8%" },
    { name: "M. Okafor", sport: "HYROX", readiness: 78, trend: "+1.4%" },
    { name: "L. Hart", sport: "Running", readiness: 92, trend: "+3.0%" },
  ];
  return (
    <div
      className="w-full overflow-hidden"
      style={{
        borderRadius: 6,
        border: "1px solid var(--mk-line-2)",
        background: "#ffffff",
        boxShadow: "0 30px 70px rgba(15,23,42,0.12)",
      }}
    >
      {/* Window chrome */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b"
        style={{ borderColor: "var(--mk-line)" }}
      >
        <span className="mk-mono text-[10px]" style={{ color: "var(--mk-dim)" }}>
          your-brand.inputperformance.com
        </span>
        <span className="mk-label" style={{ fontSize: 8, color: "var(--mk-signal)" }}>
          White label
        </span>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-px" style={{ background: "var(--mk-line)" }}>
        {[
          ["Clients", "24"],
          ["MRR share", "£182"],
          ["Adherence", "87%"],
        ].map(([label, value]) => (
          <div key={label} className="p-4" style={{ background: "#ffffff" }}>
            <div className="mk-label mb-1.5" style={{ fontSize: 8 }}>{label}</div>
            <div className="mk-mono text-[20px] font-medium" style={{ color: "var(--mk-ink)" }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Client rows */}
      <div className="border-t" style={{ borderColor: "var(--mk-line)" }}>
        <div className="px-4 pt-3 pb-2 mk-label" style={{ fontSize: 8 }}>
          Clients — today's readiness
        </div>
        {clients.map((c) => (
          <div
            key={c.name}
            className="flex items-center gap-3 px-4 py-3 border-t"
            style={{ borderColor: "var(--mk-line)" }}
          >
            <span className="text-[13px] font-medium w-24" style={{ color: "var(--mk-ink)" }}>
              {c.name}
            </span>
            <span className="mk-label hidden sm:block w-20" style={{ fontSize: 8 }}>
              {c.sport}
            </span>
            <div className="flex-1 h-1 overflow-hidden" style={{ background: "var(--mk-line)", borderRadius: 2 }}>
              <div
                style={{
                  width: `${c.readiness}%`,
                  height: "100%",
                  background: c.readiness >= 70 ? "var(--mk-signal)" : "var(--mk-amber)",
                }}
              />
            </div>
            <span className="mk-mono text-[11px] font-medium w-7 text-right" style={{ color: "var(--mk-ink)" }}>
              {c.readiness}
            </span>
            <span
              className="mk-mono text-[10px] w-12 text-right"
              style={{ color: c.trend.startsWith("+") ? "var(--mk-signal)" : "var(--mk-amber)" }}
            >
              {c.trend}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const [mailingEmail, setMailingEmail] = useState("");
  const [mailingStatus, setMailingStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [mailingError, setMailingError] = useState("");

  const joinMailingList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mailingEmail.trim()) return;
    setMailingStatus("loading");
    setMailingError("");
    try {
      const res = await fetch(`${ENV.EDGE_BASE}/join-mailing-list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ENV.SUPABASE_ANON ?? "",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email: mailingEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setMailingStatus("success");
      setMailingEmail("");
    } catch (err: any) {
      setMailingStatus("error");
      setMailingError(err.message || "Something went wrong");
    }
  };

  return (
    <div className="mk">
      <MarketingHeader />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: "var(--mk-line)" }}>
        {/* Blueprint grid, fading out towards the bottom */}
        <div
          className="absolute inset-0 mk-grid-bg pointer-events-none"
          style={{ maskImage: "linear-gradient(to bottom, black 30%, transparent 95%)", WebkitMaskImage: "linear-gradient(to bottom, black 30%, transparent 95%)", opacity: 0.6 }}
        />
        <div className="relative max-w-screen-xl mx-auto px-5 pt-20 md:pt-28 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
            <div>
              <div className="mk-label mb-7" style={{ color: "var(--mk-signal)" }}>
                Training / Nutrition / Recovery — one system
              </div>
              <h1 className="mk-display" style={{ fontSize: "clamp(44px, 8vw, 92px)" }}>
                Everything in.
                <br />
                Performance out.
              </h1>
              <p
                className="mt-8 max-w-xl text-[16px] md:text-[17px] leading-relaxed"
                style={{ color: "var(--mk-muted)" }}
              >
                Your wearables, training and nutrition, connected in one system —
                the full picture of what your body's doing and what it needs
                next. The AI coaching is just what falls out of it.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="mk-btn mk-btn-solid">
                  Download for iOS
                </a>
                <Link to="/partner/waitlist" className="mk-btn mk-btn-ghost">
                  Become a partner
                </Link>
              </div>

              <div className="mk-mono mt-6 text-[11px]" style={{ color: "var(--mk-dim)" }}>
                £2.99/week after free trial — cancel anytime
              </div>

              {/* Stat readout */}
              <div
                className="mt-14 grid grid-cols-3 max-w-lg border-t"
                style={{ borderColor: "var(--mk-line-2)" }}
              >
                {[
                  ["4,000+", "workouts analysed"],
                  ["06", "sport types"],
                  ["500+", "active athletes"],
                ].map(([value, label]) => (
                  <div key={label} className="pt-4 pr-6">
                    <div className="mk-mono text-[26px] font-medium" style={{ color: "var(--mk-ink)" }}>
                      {value}
                    </div>
                    <div className="mk-label mt-1" style={{ fontSize: 9 }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src="/screens/hero-readiness.webp"
                alt="Input home screen — sleep, readiness and strain rings, calories burned, and the coach's readiness insight"
                className="w-full block"
                style={{ maxWidth: 420 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TELEMETRY STRIP ─────────────────────────────────── */}
      <div className="mk-marquee py-3" aria-hidden>
        <div className="mk-marquee-track">
          {[0, 1].map((copy) => (
            <span key={copy}>
              {TELEMETRY.map((item, i) => (
                <span key={i} className="mk-mono text-[11px]" style={{ color: "var(--mk-dim)", letterSpacing: "0.1em" }}>
                  <span className="mx-8" style={{ color: "var(--mk-signal)" }}>+</span>
                  {item}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ── THE SYSTEM ──────────────────────────────────────── */}
      <section id="system" className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <SectionMark index="00" label="The system" />
          <h2 className="mk-display mb-16" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
            Everything connected. One complete picture.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--mk-line)" }}>
            {[
              {
                n: "01",
                title: "Sign up",
                body: "Goals, sports, biometrics. Two minutes, then the trial starts.",
              },
              {
                n: "02",
                title: "Connect your data",
                body: "Garmin, WHOOP, Apple Health — or log manually. The more it sees, the sharper it gets.",
              },
              {
                n: "03",
                title: "Train off the readout",
                body: "Programs, macros and recovery calls that shift with your actual state, every day.",
              },
            ].map((step) => (
              <div key={step.n} className="p-8 md:p-10" style={{ background: "var(--mk-carbon)" }}>
                <div className="mk-mono text-[13px] font-medium mb-6" style={{ color: "var(--mk-signal)" }}>
                  {step.n}
                </div>
                <h3 className="mk-display mb-3" style={{ fontSize: 20 }}>
                  {step.title}
                </h3>
                <p className="text-[14px] leading-relaxed" style={{ color: "var(--mk-muted)" }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VITALS ──────────────────────────────────────────── */}
      <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <SectionMark index="01" label="Vitals" />
          <h2 className="mk-display mb-6" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
            Every signal your body sends.
          </h2>
          <p className="text-[15.5px] leading-relaxed max-w-xl mb-14" style={{ color: "var(--mk-muted)" }}>
            Resting heart rate, HRV, SpO₂ and respiratory rate stream in from
            Garmin and WHOOP, tracked against your own 30-day baseline — not
            population averages. Every reading in context, with what it means
            for today spelled out in plain English.
          </p>

          <VitalsRail />
        </div>
      </section>

      <FeatureSection
        index="02"
        label="Training"
        title="Programs that do the maths."
        mock={<TrainingShots />}
        flip
      >
        <p>
          Interval paces from your 5K PB. Bar loads from your 1RMs. Programs
          for running, cycling, swimming, triathlon, CrossFit and HYROX that
          progress week to week — and back off when the data says so.
        </p>
        <Bullets
          items={[
            "Paces and percentages computed, not guessed",
            "Progressive overload handled session to session",
            "Race calendar with target events built in",
            "Tick off sections mid-workout, coach debriefs after",
          ]}
        />
      </FeatureSection>

      {/* ── NUTRITION (full-width showcase) ─────────────────── */}
      <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
            <div>
              <SectionMark index="03" label="Nutrition" />
              <h2 className="mk-display mb-6" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
                Macros without the spreadsheet.
              </h2>
              <p className="text-[15.5px] leading-relaxed max-w-lg" style={{ color: "var(--mk-muted)" }}>
                Scan a barcode, photograph a plate, or just say what you ate —
                the coach logs it, scores it, and tells you what to eat next.
                Daily targets move with your training and recovery instead of
                sitting frozen from the day you signed up.
              </p>
            </div>
            <Bullets
              items={[
                "Barcode, photo, voice and search logging",
                "A daily food score, not just a calorie count",
                "Fibre, sugar, salt and saturated fat tracked too",
                "Coach breaks down what shaped your score",
                "Post-workout refuelling plans with exact numbers",
              ]}
            />
          </div>

          <img
            src="/screens/nutrition-showcase.webp"
            alt="Nutrition in the app — daily food score, macro rings, logged meals, a coach breakdown of what shaped the score, and micro targets"
            loading="lazy"
            className="w-full block mt-12 md:mt-16"
            style={{ maxWidth: 1200, margin: "0 auto" }}
          />
        </div>
      </section>

      {/* ── SPEC SHEET ──────────────────────────────────────── */}
      <section id="spec" className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <SectionMark index="04" label="Specification" />
          <h2 className="mk-display mb-4" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
            The full spec.
          </h2>
          <p className="text-[15px] max-w-xl mb-12" style={{ color: "var(--mk-muted)" }}>
            One subscription. Everything below ships in the app.
          </p>

          <div className="border-t" style={{ borderColor: "var(--mk-line)" }}>
            {SPEC.map((row) => (
              <div key={row.id} className="mk-spec-row">
                <span className="mk-mono text-[12px]" style={{ color: "var(--mk-signal)" }}>
                  {row.id}
                </span>
                <span className="mk-display" style={{ fontSize: 17 }}>
                  {row.name}
                </span>
                <span className="mk-spec-desc text-[14px] leading-relaxed" style={{ color: "var(--mk-muted)" }}>
                  {row.desc}
                </span>
                <span className="mk-spec-tag mk-label" style={{ fontSize: 10 }}>
                  {row.tag}
                </span>
              </div>
            ))}
          </div>

          <div className="mk-mono mt-8 text-[11px]" style={{ color: "var(--mk-dim)", letterSpacing: "0.1em" }}>
            Integrates with — Garmin · WHOOP · Apple Health · Concept2
          </div>
        </div>
      </section>

      {/* ── PARTNER ─────────────────────────────────────────── */}
      <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
            <div>
              <SectionMark index="05" label="Partners" />
              <h2 className="mk-display mb-6" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
                Put your name on it.
              </h2>
              <p className="text-[15.5px] leading-relaxed max-w-lg" style={{ color: "var(--mk-muted)" }}>
                Coaches, gyms and PTs run the same engine under their own
                brand — client dashboards, program building, nutrition plans —
                and keep <span style={{ color: "var(--mk-signal)" }}>20% of the revenue</span>, every month.
              </p>
              <Bullets
                items={[
                  "20% recurring commission per client",
                  "Your logo, your colours, your coach",
                  "Client progress and recovery dashboards",
                  "Team and corporate wellness packages",
                ]}
              />
              <Link to="/partner/waitlist" className="mk-btn mk-btn-solid mt-10">
                Apply as a partner
              </Link>
            </div>
            <PartnerDashboardMock />
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────── */}
      <section id="pricing" className="border-b" style={{ borderColor: "var(--mk-line)" }}>
        <div className="max-w-screen-xl mx-auto px-5 py-20 md:py-28">
          <SectionMark index="06" label="Pricing" />
          <h2 className="mk-display mb-14" style={{ fontSize: "clamp(30px, 4.5vw, 46px)" }}>
            One price. No tiers.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px max-w-4xl" style={{ background: "var(--mk-line)" }}>
            {/* Member */}
            <div className="p-8 md:p-12 flex flex-col" style={{ background: "var(--mk-panel)" }}>
              <div className="mk-label mb-6">Member</div>
              <div className="flex items-baseline gap-2">
                <span className="mk-mono text-[44px] font-medium" style={{ color: "var(--mk-ink)" }}>
                  £2.99
                </span>
                <span className="mk-mono text-[13px]" style={{ color: "var(--mk-muted)" }}>
                  /week
                </span>
              </div>
              <ul className="mt-8 mb-10 flex flex-col flex-1">
                {["Free trial — no card required", "Full coach, training and nutrition access", "Cancel anytime in-app"].map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 items-baseline py-3 border-t text-[14px]"
                    style={{ borderColor: "var(--mk-line)", color: "var(--mk-muted)" }}
                  >
                    <span className="mk-mono text-[10px]" style={{ color: "var(--mk-signal)" }}>+</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="mk-btn mk-btn-solid w-full">
                Download the app
              </a>
            </div>

            {/* Partner */}
            <div className="p-8 md:p-12 flex flex-col" style={{ background: "var(--mk-carbon)" }}>
              <div className="mk-label mb-6">Partner</div>
              <div className="flex items-baseline gap-2">
                <span className="mk-mono text-[44px] font-medium" style={{ color: "var(--mk-ink)" }}>
                  20%
                </span>
                <span className="mk-mono text-[13px]" style={{ color: "var(--mk-muted)" }}>
                  rev share
                </span>
              </div>
              <ul className="mt-8 mb-10 flex flex-col flex-1">
                {["White-label branding included", "Recurring commission on every client", "Dashboard, builders and analytics"].map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 items-baseline py-3 border-t text-[14px]"
                    style={{ borderColor: "var(--mk-line)", color: "var(--mk-muted)" }}
                  >
                    <span className="mk-mono text-[10px]" style={{ color: "var(--mk-signal)" }}>+</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/partner/waitlist" className="mk-btn mk-btn-ghost w-full">
                Apply now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAILING LIST ────────────────────────────────────── */}
      <section>
        <div className="max-w-screen-xl mx-auto px-5 py-20">
          <div className="max-w-2xl">
            <div className="mk-label mb-4">Dispatches</div>
            <h2 className="mk-display mb-4" style={{ fontSize: "clamp(24px, 3.5vw, 34px)" }}>
              Training notes, not newsletters.
            </h2>
            <p className="text-[14px] mb-8" style={{ color: "var(--mk-muted)" }}>
              Product updates and the occasional thing worth reading. No drip campaigns.
            </p>
            <form onSubmit={joinMailingList} className="flex gap-3 flex-wrap">
              <input
                type="email"
                required
                placeholder="you@email.com"
                value={mailingEmail}
                onChange={(e) => {
                  setMailingEmail(e.target.value);
                  if (mailingStatus !== "idle") setMailingStatus("idle");
                }}
                disabled={mailingStatus === "loading" || mailingStatus === "success"}
                style={{ flex: "1 1 260px" }}
              />
              <button
                type="submit"
                disabled={mailingStatus === "loading" || mailingStatus === "success"}
                className="mk-btn mk-btn-solid"
                style={mailingStatus === "loading" ? { opacity: 0.6 } : undefined}
              >
                {mailingStatus === "loading" ? "Joining…" : mailingStatus === "success" ? "You're in" : "Subscribe"}
              </button>
            </form>
            {mailingStatus === "error" && (
              <p className="mk-mono mt-4 text-[12px]" style={{ color: "var(--mk-red)" }}>
                {mailingError}
              </p>
            )}
            {mailingStatus === "success" && (
              <p className="mk-mono mt-4 text-[12px]" style={{ color: "var(--mk-signal)" }}>
                Subscribed — we'll keep it rare
              </p>
            )}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
